<template>
    <!-- own root: v-menu wraps the button in a div of its own, this one is the item of the clock row -->
    <div class="p-live-screen-wrap">
        <v-menu offset-y :z-index="20">
            <template v-slot:activator="{ on, attrs }">
                <button
                    type="button"
                    class="p-live-screen"
                    :class="[`p-live-screen--${pageOpen ? 'on' : 'off'}`, { 'p-live-screen--failed': failed }]"
                    title="Screen of the live stream: tap to change it"
                    v-bind="attrs"
                    v-on="on"
                >
                    <v-icon small class="p-live-screen__icon">mdi-monitor-screenshot</v-icon>
                    <span class="p-live-screen__label">On stream</span>
                    <span class="p-live-screen__value">
                        <span class="p-live-screen__dot" />
                        {{ statusText }}
                    </span>
                    <v-icon small>mdi-menu-down</v-icon>
                </button>
            </template>
            <v-list dense class="py-0">
                <v-subheader class="font-weight-bold">Show on the stream</v-subheader>
                <v-list-item
                    v-for="item in selectable"
                    :key="item.value"
                    :input-value="item.value === screen"
                    color="primary"
                    @click="choose(item.value)"
                >
                    <v-list-item-icon class="mr-3"><v-icon small>{{ item.icon }}</v-icon></v-list-item-icon>
                    <v-list-item-title class="font-weight-bold">{{ item.text }}</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>
    </div>
</template>

<script>
    import matchService from '@/services/match'

    const SCREENS = [
        { value: 'pregame', text: 'Pregame', icon: 'mdi-information-outline', selectable: true },
        { value: 'live-score', text: 'Live score', icon: 'mdi-scoreboard-outline', selectable: true },
        { value: 'starters', text: 'Starters', icon: 'mdi-account-group-outline', selectable: true },
        { value: 'best-performer', text: 'Best performer', icon: 'mdi-star-outline', selectable: false },
        { value: 'timeout', text: 'Timeout', icon: 'mdi-timer-pause-outline', selectable: true },
        { value: 'half-time', text: 'Half time', icon: 'mdi-circle-half-full', selectable: true },
        { value: 'full-time', text: 'Full time', icon: 'mdi-flag-checkered', selectable: true }
    ]
    const POLL_MS = 3000
    // the stream page reports every 20s: without a report for this long it is not open
    const STALE_MS = 45000

    // What the live stream page (LiveData) shows right now, as it reports it, and a menu to switch it
    export default {
        name: 'StatsLiveScreen',
        props: {
            matchId: {
                type: [Number, String],
                required: true
            }
        },
        data() {
            return {
                selectable: SCREENS.filter(item => item.selectable),
                screen: null,
                pageOpen: false,
                timer: null,
                inFlight: false,
                failed: false,
                failedTimer: null
            }
        },
        computed: {
            statusText() {
                if (this.failed) return 'Change failed'
                if (!this.pageOpen) return 'Not open'
                const item = SCREENS.find(entry => entry.value === this.screen)
                return item ? item.text : this.screen
            }
        },
        created() {
            this.fetch()
            this.timer = setInterval(() => {
                if (!document.hidden) this.fetch()
            }, POLL_MS)
        },
        beforeDestroy() {
            clearInterval(this.timer)
            clearTimeout(this.failedTimer)
        },
        methods: {
            fetch() {
                if (this.inFlight) return
                this.inFlight = true
                matchService.getLiveScreen(this.matchId).then(resp => {
                    const data = resp.data || {}
                    this.screen = data.screen || null
                    this.pageOpen = !!data.screen && !!data.reportedAt && data.serverTime - data.reportedAt < STALE_MS
                }).catch(() => {}).finally(() => {
                    this.inFlight = false
                })
            },
            choose(screen) {
                // a timeout stops the game: the clock stops with the screen
                if (screen === 'timeout') this.$emit('pause-clock')
                matchService.setLiveScreen(this.matchId, screen).then(() => {
                    // the page reports its new screen right away
                    setTimeout(this.fetch, 800)
                }).catch(() => {
                    this.failed = true
                    clearTimeout(this.failedTimer)
                    this.failedTimer = setTimeout(() => {
                        this.failed = false
                    }, 3000)
                })
            }
        }
    }
</script>

<style scoped>
.p-live-screen-wrap {
    flex-basis: 100%;
}
/* the stacked (landscape) clock is a grid: a row of its own */
.l-clock .p-live-screen-wrap {
    grid-column: 1 / -1;
}
.p-live-screen {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    height: 34px;
    padding: 0 8px 0 10px;
    border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    background: #fff;
    font-size: 13px;
    line-height: 1;
}
.p-live-screen__label {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.5);
}
.p-live-screen__value {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-left: auto;
    font-weight: 800;
}
.p-live-screen__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
}
.p-live-screen--on .p-live-screen__dot {
    background: #e53935;
    animation: live-screen-pulse 1.4s ease-in-out infinite;
}
.p-live-screen--off .p-live-screen__dot {
    background: rgba(0, 0, 0, 0.3);
}
.p-live-screen--failed .p-live-screen__value {
    color: #e53935;
}
.p-live-screen--off .p-live-screen__value {
    color: rgba(0, 0, 0, 0.45);
}
@keyframes live-screen-pulse {
    50% {
        opacity: 0.35;
    }
}
</style>
